import { mediaUrl, useSiteMedia } from "../lib/siteMedia";
import React, { useState } from "react";
import { ChevronRight, Users, Clock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import SectionHeader from "../components/SectionHeader";

const SPACES = [
  { id: "boardroom", name: "The Board Room", capacity: "Seats 14", img: "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", desc: "Walnut conference table, six-screen wall, soundproofed. Built for the closing." },
  { id: "podcast", name: "The Podcast Studio", capacity: "Up to 4 guests", img: "https://images.pexels.com/photos/7383469/pexels-photo-7383469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600", desc: "Broadcast-grade Shure SM7B mics, 4K cameras, soundproof room. Engineer on call." },
  { id: "conference", name: "Executive Conference", capacity: "Seats 8", img: "https://images.unsplash.com/photo-1679364297777-1db77b6199be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", desc: "Daylit room for board agendas, investor reviews, and quiet first conversations." },
  { id: "members-lounge", name: "Members Lounge", capacity: "Up to 30", img: "https://images.unsplash.com/photo-1617364852223-75f57e78dc96?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", desc: "Leather chairs, marble bar, fireplace. The room where most evenings begin and end." },
  { id: "vip-lounge", name: "VIP Lounge", capacity: "Up to 12", img: "https://images.unsplash.com/photo-1773080521729-18239e46f50a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", desc: "Reserved for Founding members and their guests. Tableside humidor service." },
  { id: "private-humidor", name: "Private Humidor", capacity: "By appointment", img: "https://images.unsplash.com/photo-1780108647891-705d4b1259d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", desc: "Climate-controlled walk-in with members' private storage and rare allocations." },
];

export default function PrivateSpaces() {
  const media = useSiteMedia();
  return (
    <div data-testid="private-spaces-page">
      <section className="relative pt-32 min-h-[70vh] flex items-end px-6 sm:px-10 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={mediaUrl(media, "spaces_hero")} alt="" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-[1600px] mx-auto pb-16 w-full">
          <p className="eyebrow">Private Spaces</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
            Rooms that <span className="italic" style={{ color: "var(--twr-bronze)" }}>mean business.</span>
          </h1>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1600px] mx-auto">
          <SectionHeader eyebrow="The Rooms" title="Where deals find their footing." kicker="Bookable by Executive, Founders, and Corporate members." />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {SPACES.map((s) => <SpaceCard key={s.id} s={{ ...s, img: mediaUrl(media, `space_${s.id.replace(/-/g, "_")}`) }} />)}
          </div>
        </div>
      </section>
    </div>
  );
}

function SpaceCard({ s }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", space: s.id,
    date: "", start_time: "", duration_hours: 2, attendees: 4, notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/private-spaces/book", form);
      setDone(true);
      toast.success("Booking request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <article data-testid={`space-card-${s.id}`} className="surface overflow-hidden flex flex-col">
      <div className="aspect-[4/3] overflow-hidden">
        <img src={s.img} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
      </div>
      <div className="p-7 flex flex-col flex-1">
        <h3 className="font-serif text-2xl">{s.name}</h3>
        <p className="text-[var(--twr-warm-white)]/65 text-sm mt-3 flex-1 leading-relaxed">{s.desc}</p>
        <div className="flex items-center gap-2 text-[12px] text-[var(--twr-bronze)] mt-5 tracking-[0.22em] uppercase">
          <Users size={13} /> {s.capacity}
        </div>
        <button onClick={() => setOpen((v) => !v)} data-testid={`space-book-${s.id}`}
          className="mt-6 btn-ghost">
          {open ? "Close" : "Request Booking"} <ChevronRight size={13} />
        </button>
        {open && !done && (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid={`space-form-${s.id}`}>
            <Input label="Name" v={form.name} on={(v) => set("name", v)} required />
            <Input label="Email" type="email" v={form.email} on={(v) => set("email", v)} required />
            <Input label="Phone" v={form.phone} on={(v) => set("phone", v)} required />
            <Input label="Company" v={form.company} on={(v) => set("company", v)} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Date" type="date" v={form.date} on={(v) => set("date", v)} required />
              <Input label="Start" type="time" v={form.start_time} on={(v) => set("start_time", v)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Hours" type="number" v={form.duration_hours} on={(v) => set("duration_hours", parseInt(v) || 1)} />
              <Input label="Attendees" type="number" v={form.attendees} on={(v) => set("attendees", parseInt(v) || 1)} />
            </div>
            <button disabled={loading} className="btn-bronze w-full">
              {loading ? "Submitting…" : "Submit Request"}
            </button>
          </form>
        )}
        {done && <p className="text-[var(--twr-bronze)] mt-4 text-sm">Booking request received. We'll be in touch.</p>}
      </div>
    </article>
  );
}

function Input({ label, type = "text", v, on, required }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">{label}</span>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} required={required}
        className="mt-1.5 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-2.5 text-sm transition-colors" />
    </label>
  );
}
